/**
 * @fileoverview Hook de UI Layer para la gestión de estado y acciones de la interfaz de negocio del Inbox.
 * Permite manejar cambios de filtro y ordenamiento, así como disparar la recarga de conversaciones y el reseteo de la conversación seleccionada.
 * Cumple con la estricta separación de responsabilidades de UI Layer en Mercately.
 *
 * @module useInboxBusinessUI
 * @category Hook - UI Layer
 * @since 2.0.0
 * @version 3.1.0
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

/**
 * Hook de UI Layer para la gestión de estado y acciones de la interfaz de negocio del Inbox.
 *
 * Implementa el patrón de **UI Layer** con separación estricta de responsabilidades, enfocándose únicamente en la gestión de eventos y handlers de la interfaz de usuario.
 */
const useInboxBusinessUI = ({
  filter,
  updateFilter,
  updateSortConfig,
  loadConversations,
  sidebar = false
}) => {
  const dispatch = useDispatch();

  /**
   * Efecto para recargar conversaciones al cambiar filtros principales.
   */
  useEffect(() => {
    // Debounce para evitar consultas concurrentes
    const handler = setTimeout(() => {
      if (sidebar) return; // Evitar recarga automática en sidebar
      console.log('loadConversations useInboxBusinessUI');
      loadConversations();
    }, 500);
    return () => clearTimeout(handler);
  }, [
    filter.conversation,
    filter.mia,
    filter.channel,
    loadConversations,
    sidebar
  ]);

  /**
   * Handler para cambio de filtro en la UI.
   *
   * Resetea la conversación seleccionada en Redux y actualiza el filtro correspondiente.
   */
  const handleFilterChange = useCallback((section, filterKey) => {
    updateFilter(section, filterKey);
  }, [updateFilter, dispatch]);

  /**
   * Handler para cambio de ordenamiento en la UI.
   */
  const handleSortChange = useCallback((newSortConfig) => {
    updateSortConfig(newSortConfig);
  }, [updateSortConfig]);

  /**
   * Retorna handlers de UI memoizados para cambios de filtro y orden.
   *
   * @returns {UseInboxBusinessUIResult} Handlers de UI
   */
  return useMemo(() => ({
    handleFilterChange,
    handleSortChange,
  }), [handleFilterChange, handleSortChange]);
};

export default useInboxBusinessUI;
